import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Shell from "@/components/layout/Shell";
import { api } from "@/lib/api";
import { PageHeader, PrimaryButton, GhostButton, Field, inputCls } from "@/components/common";
import { StatusPill, ProgressBar, STATUS_LIST } from "@/components/projectShared";
import { ArrowLeft, Plus, Trash2, ListChecks, Activity, CalendarDays, User as UserIcon, Store, Send, Edit3, BookmarkPlus, Repeat, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState("");
  const [newUpdate, setNewUpdate] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [team, setTeam] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, tm] = await Promise.all([api.get(`/projects/${id}`), api.get("/team")]);
      setP(data); setTeam(tm.data);
    } catch { toast.error("Proyecto no encontrado"); navigate("/proyectos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const changeStatus = async (status) => {
    try { await api.patch(`/projects/${id}`, { status }); toast.success("Estado actualizado"); load(); }
    catch { toast.error("No se pudo actualizar"); }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    try { await api.post(`/projects/${id}/tasks`, { title: newTask }); setNewTask(""); load(); }
    catch { toast.error("No se pudo añadir la tarea"); }
  };

  const toggleTask = async (t) => {
    try { await api.patch(`/projects/${id}/tasks/${t.task_id}`, { done: !t.done }); load(); }
    catch { toast.error("No se pudo actualizar la tarea"); }
  };

  const removeTask = async (t) => {
    if (!window.confirm("¿Eliminar esta tarea?")) return;
    await api.delete(`/projects/${id}/tasks/${t.task_id}`); load();
  };

  const addUpdate = async (e) => {
    e.preventDefault();
    if (!newUpdate.trim()) return;
    try { await api.post(`/projects/${id}/updates`, { text: newUpdate }); setNewUpdate(""); toast.success("Avance registrado"); load(); }
    catch { toast.error("No se pudo publicar"); }
  };

  const removeUpdate = async (u) => {
    if (!window.confirm("¿Eliminar esta actualización?")) return;
    await api.delete(`/projects/${id}/updates/${u.update_id}`); load();
  };

  const openEdit = () => {
    setEditForm({
      name: p.name, description: p.description || "",
      status: p.status, team_member_id: p.team_member_id || "",
      start_date: p.start_date || "", due_date: p.due_date || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm };
      if (!payload.team_member_id) payload.team_member_id = null;
      Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
      await api.patch(`/projects/${id}`, payload);
      toast.success("Actualizado"); setEditOpen(false); load();
    } catch { toast.error("No se pudo guardar"); }
  };

  const removeProject = async () => {
    if (!window.confirm(`¿Eliminar el proyecto "${p.name}"?`)) return;
    await api.delete(`/projects/${id}`);
    toast.success("Proyecto eliminado"); navigate("/proyectos");
  };

  const saveAsTemplate = async () => {
    if (!p.tasks || p.tasks.length === 0) { toast.error("El proyecto no tiene tareas para plantillar"); return; }
    const name = window.prompt("Nombre de la plantilla:", p.name);
    if (!name) return;
    try {
      await api.post(`/projects/${id}/save-as-template`, { name, include_done: true });
      toast.success("Plantilla guardada. La verás al crear un nuevo proyecto.");
    } catch { toast.error("No se pudo guardar la plantilla"); }
  };

  if (loading || !p) return <Shell><div className="text-[#9CA3AF]">Cargando…</div></Shell>;

  return (
    <Shell>
      <button
        data-testid="back-to-projects"
        onClick={()=>navigate("/proyectos")}
        className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-[#F5F5F7] mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75}/> Volver a Proyectos
      </button>

      {/* Header card */}
      <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <StatusPill status={p.status}/>
              {p.client && (
                (() => {
                  const isMensual = p.client.billing_type !== "unico";
                  const KindIcon = isMensual ? Repeat : Package;
                  return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${
                      isMensual ? "bg-[#F97316]/10 text-[#F97316] border-[#F97316]/25" : "bg-purple-500/10 text-purple-300 border-purple-500/25"
                    }`}>
                      <KindIcon className="w-3 h-3"/>
                      {isMensual ? "Consultoría continua" : "Entrega única"}
                    </span>
                  );
                })()
              )}
              <span className="text-[11px] text-[#9CA3AF]">
                Actualizado {new Date(p.updated_at).toLocaleDateString("es-CO",{day:"numeric", month:"short", year:"numeric"})}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#F5F5F7] tracking-tight break-words" style={{ fontFamily: "Manrope, sans-serif" }}>
              {p.name}
            </h1>
            {p.description && <p className="text-sm text-[#9CA3AF] mt-2 max-w-3xl whitespace-pre-wrap">{p.description}</p>}
            <div className="flex items-center gap-4 mt-4 flex-wrap text-xs text-[#9CA3AF]">
              {p.client && (
                <button onClick={()=>navigate(`/clientes/${p.client.client_id}`)} className="inline-flex items-center gap-1.5 hover:text-[#F97316] transition-colors">
                  <Store className="w-3.5 h-3.5"/> {p.client.name}
                </button>
              )}
              {p.team_member && (
                <span className="inline-flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5"/> {p.team_member.name}</span>
              )}
              {p.start_date && (
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5"/> Inicio {p.start_date}</span>
              )}
              {p.due_date && (
                <span className="inline-flex items-center gap-1.5 text-[#F97316]"><CalendarDays className="w-3.5 h-3.5"/> Entrega {p.due_date}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <GhostButton data-testid="save-as-template-btn" onClick={saveAsTemplate}><BookmarkPlus className="w-4 h-4"/> Guardar plantilla</GhostButton>
            <GhostButton data-testid="edit-project-btn" onClick={openEdit}><Edit3 className="w-4 h-4"/> Editar</GhostButton>
            <button onClick={removeProject} className="inline-flex items-center gap-2 bg-transparent border border-red-500/30 hover:bg-red-500/10 text-red-400 font-medium rounded-xl px-3 py-2 text-sm transition-colors" data-testid="delete-project-btn">
              <Trash2 className="w-4 h-4"/> Eliminar
            </button>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#9CA3AF]">Progreso · {p.tasks_done}/{p.task_count} tareas</span>
            <span className="text-lg font-bold text-[#F97316]" style={{ fontFamily: "Manrope, sans-serif" }}>{p.progress}%</span>
          </div>
          <ProgressBar value={p.progress}/>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {STATUS_LIST.map(s => (
            <button
              key={s.key}
              data-testid={`status-btn-${s.key}`}
              onClick={()=>changeStatus(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                p.status === s.key
                  ? "bg-[#F97316]/10 border-[#F97316]/40 text-[#F97316]"
                  : "border-[#262A33] text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#1A1D24]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid: Tasks + Updates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks */}
        <section className="lg:col-span-2 bg-[#15171E] border border-[#262A33] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <ListChecks className="w-4 h-4 text-[#F97316]"/>
            <h2 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {p.client?.billing_type === "unico" ? "Entregables del proyecto" : "Tareas y entregas semanales"}
            </h2>
          </div>
          <form onSubmit={addTask} className="flex gap-2 mb-4">
            <input
              data-testid="new-task-input"
              className={`${inputCls} flex-1`}
              placeholder="Añade una tarea (Ej: Diseñar carrusel Instagram)"
              value={newTask}
              onChange={e=>setNewTask(e.target.value)}
            />
            <PrimaryButton data-testid="add-task-btn" type="submit"><Plus className="w-4 h-4"/></PrimaryButton>
          </form>

          {p.tasks.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-6">Aún no hay tareas. Añade la primera arriba.</p>
          ) : (
            <div className="space-y-2">
              {p.tasks.map(t => (
                <div key={t.task_id} data-testid={`task-row-${t.task_id}`} className="flex items-center gap-3 p-3 rounded-xl bg-[#0B0D12] border border-[#262A33] group">
                  <button
                    data-testid={`toggle-task-${t.task_id}`}
                    onClick={()=>toggleTask(t)}
                    aria-label={t.done ? "Marcar pendiente" : "Marcar completa"}
                    className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                      t.done ? "bg-[#F97316] border-[#F97316]" : "border-[#3A3F4B] hover:border-[#F97316]"
                    }`}
                  >
                    {t.done && (
                      <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,6 5,9 10,3"/>
                      </svg>
                    )}
                  </button>
                  <span className={`flex-1 text-sm ${t.done ? "line-through text-[#6B7280]" : "text-[#F5F5F7]"}`}>{t.title}</span>
                  {t.done && t.done_at && (
                    <span className="text-[10px] text-[#6B7280]">
                      {new Date(t.done_at).toLocaleDateString("es-CO",{day:"numeric",month:"short"})}
                    </span>
                  )}
                  <button
                    onClick={()=>removeTask(t)}
                    aria-label="Eliminar"
                    className="opacity-0 group-hover:opacity-100 p-1 rounded text-[#6B7280] hover:text-red-400 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Updates */}
        <section className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-[#F97316]"/>
            <h2 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {p.client?.billing_type === "unico" ? "Historial de avances" : "Notas semanales de consultoría"}
            </h2>
          </div>
          <form onSubmit={addUpdate} className="mb-4">
            <textarea
              data-testid="new-update-input"
              rows={3}
              className={inputCls}
              placeholder={p.client?.billing_type === "unico" ? "¿Qué se avanzó en el entregable?" : "¿Qué se trabajó esta semana con el cliente?"}
              value={newUpdate}
              onChange={e=>setNewUpdate(e.target.value)}
            />
            <PrimaryButton data-testid="add-update-btn" type="submit" className="w-full mt-2 justify-center">
              <Send className="w-4 h-4"/> {p.client?.billing_type === "unico" ? "Publicar avance" : "Publicar nota semanal"}
            </PrimaryButton>
          </form>

          {p.updates.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-4">Sin avances publicados todavía.</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-auto pr-1">
              {p.updates.map(u => (
                <div key={u.update_id} data-testid={`update-row-${u.update_id}`} className="p-3 rounded-xl bg-[#0B0D12] border border-[#262A33] group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-[#F97316] font-medium">{u.author}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#6B7280]">
                        {new Date(u.created_at).toLocaleString("es-CO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                      </span>
                      <button onClick={()=>removeUpdate(u)} aria-label="Eliminar" className="opacity-0 group-hover:opacity-100 text-[#6B7280] hover:text-red-400 transition-all">
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-[#F5F5F7] whitespace-pre-wrap">{u.text}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Editar proyecto</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <Field label="Nombre *">
              <input required className={inputCls} value={editForm.name || ""} onChange={e=>setEditForm({...editForm, name: e.target.value})}/>
            </Field>
            <Field label="Descripción">
              <textarea rows={3} className={inputCls} value={editForm.description || ""} onChange={e=>setEditForm({...editForm, description: e.target.value})}/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Estado">
                <select className={inputCls} value={editForm.status} onChange={e=>setEditForm({...editForm, status: e.target.value})}>
                  {STATUS_LIST.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Responsable">
                <select className={inputCls} value={editForm.team_member_id || ""} onChange={e=>setEditForm({...editForm, team_member_id: e.target.value})}>
                  <option value="">(sin asignar)</option>
                  {team.map(t => <option key={t.member_id} value={t.member_id}>{t.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Inicio"><input type="date" className={inputCls} value={editForm.start_date || ""} onChange={e=>setEditForm({...editForm, start_date: e.target.value})}/></Field>
              <Field label="Entrega"><input type="date" className={inputCls} value={editForm.due_date || ""} onChange={e=>setEditForm({...editForm, due_date: e.target.value})}/></Field>
            </div>
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setEditOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton type="submit" data-testid="save-project-edit-btn">Guardar</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
